import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, TrendingUp, Users, Handshake, ArrowRight, CheckCircle, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function HomePage() {
  return <div className="w-full" data-id="element-289">
      {/* Hero Section */}
      <section className="relative bg-navy pt-28 pb-16 md:pt-36 md:pb-24 lg:pt-44 lg:pb-32 overflow-hidden" data-id="element-290">
        {/* Geometric Background Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-291">
          <div className="absolute top-20 left-10 w-64 h-64 border-2 border-gold rotate-45" data-id="element-292" />
          <div className="absolute bottom-20 right-10 w-96 h-96 border-2 border-gold rotate-12" data-id="element-293" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-gold-light rotate-6" data-id="element-294" />
        </div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10" data-id="element-295">
          <motion.div className="max-w-4xl mx-auto text-center" initial="initial" animate="animate" variants={staggerContainer} data-id="element-296">
            <motion.h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" variants={fadeInUp} data-id="element-297">
              Connecting Capital, Commodities,
              <span className="text-gold" data-id="element-298"> and Opportunity</span>
            </motion.h1>
            <motion.p className="text-base sm:text-lg md:text-xl text-steel mb-8 leading-relaxed max-w-3xl mx-auto px-2" variants={fadeInUp} data-id="element-299">
              Strategic Metals Group Inc. is a Canada-based advisory firm
              connecting qualified investors, mining ventures, operators, and
              commodity buyers across global markets.
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0" variants={fadeInUp} data-id="element-300">
              <Button asChild size="lg" className="bg-gold hover:bg-gold-dark text-navy font-semibold whitespace-nowrap" data-id="element-301">
                <Link to="/contact" className="inline-flex items-center justify-center" data-id="element-302">
                  Schedule a Consultation
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-gold text-gold hover:bg-gold hover:text-navy font-semibold whitespace-nowrap" data-id="element-303">
                <Link to="/services" className="inline-flex items-center justify-center" data-id="element-304">
                  Explore Our Services
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Band */}
      <section className="bg-white py-10 md:py-12 border-t-2 border-gold" data-id="element-305">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-306">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-307">
            {[{
            icon: Globe,
            text: 'Global Investor Network'
          }, {
            icon: TrendingUp,
            text: 'Mining & Metals Expertise'
          }, {
            icon: Handshake,
            text: 'Structured Off-Take Advisory'
          }, {
            icon: Users,
            text: 'End-to-End Strategic Partnerships'
          }].map((item, index) => <motion.div key={index} className="flex flex-col items-center text-center space-y-2 md:space-y-3" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-308">
                <item.icon className="h-8 w-8 md:h-10 md:w-10 text-gold" data-id="element-309" />
                <p className="font-semibold text-navy text-sm md:text-base" data-id="element-310">
                  {item.text}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section className="bg-ivory py-16 md:py-20" data-id="element-311">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-312">
          <motion.div className="max-w-3xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-313">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 md:mb-6" data-id="element-314">
              Who We Are
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-315" />
            <p className="text-base md:text-lg text-charcoal leading-relaxed mb-8" data-id="element-316">
              Strategic Metals Group Inc. bridges the gap between investment,
              exploration, production, and distribution. We leverage an
              extensive international network to facilitate strategic
              partnerships between early-stage explorers, developers, mine
              operators, and qualified investors seeking exposure to gold,
              silver, and other precious metals.
            </p>
            <Button asChild size="lg" className="bg-navy hover:bg-navy-light text-white font-semibold" data-id="element-317">
              <Link to="/about" className="inline-flex items-center justify-center gap-2" data-id="element-318">
                Learn More
                <ArrowRight className="h-4 w-4" data-id="element-319" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="bg-white py-16 md:py-20" data-id="element-320">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-321">
          <motion.div className="text-center mb-12 md:mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-322">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-323">
              Our Services
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-4 md:mb-6" data-id="element-324" />
            <p className="text-base md:text-lg text-charcoal max-w-2xl mx-auto" data-id="element-325">
              Comprehensive advisory solutions across the precious metals supply
              chain
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-326">
            {[{
            title: 'Investor & Capital Matchmaking',
            description: 'Connect early-stage explorers and mine operators with qualified investors seeking high-potential natural resources opportunities.',
            icon: Users
          }, {
            title: 'Strategic Off-Take Advisory',
            description: 'Secure long-term revenue channels through structured relationships with buyers in international markets, particularly the Middle East.',
            icon: Handshake
          }, {
            title: 'Mining Project Partnership',
            description: 'Structure strategic relationships between project owners, operators, capital partners, and commodity buyers.',
            icon: TrendingUp
          }, {
            title: 'Global Market Access',
            description: 'Leverage international relationships and market knowledge to unlock cross-border opportunities.',
            icon: Globe
          }, {
            title: 'Supply Chain Strategy',
            description: 'Bridge investment, exploration, production, and distribution with coordinated strategic guidance.',
            icon: CheckCircle
          }].map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={index === 3 ? 'md:col-start-1 lg:col-start-auto' : ''} data-id="element-327">
                <Card className="h-full border border-gold/20 hover:border-gold transition-all duration-300 hover:shadow-lg" data-id="element-328">
                  <CardContent className="p-6" data-id="element-329">
                    <service.icon className="h-10 w-10 md:h-12 md:w-12 text-gold mb-4" data-id="element-330" />
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-3" data-id="element-331">
                      {service.title}
                    </h3>
                    <p className="text-charcoal leading-relaxed text-sm md:text-base" data-id="element-332">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          <motion.div className="text-center mt-10 md:mt-12" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} data-id="element-333">
            <Button asChild size="lg" className="bg-gold hover:bg-gold-dark text-navy font-semibold" data-id="element-334">
              <Link to="/services" className="inline-flex items-center justify-center gap-2" data-id="element-335">
                View All Services
                <ArrowRight className="h-4 w-4" data-id="element-336" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Industries & Commodities */}
      <section className="bg-navy py-16 md:py-20" data-id="element-337">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-338">
          <motion.div className="text-center mb-10 md:mb-12" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-339">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-340">
              Commodities Focus
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto" data-id="element-341" />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto" data-id="element-342">
            {['Gold', 'Silver', 'Precious Metals', 'Natural Resources'].map((commodity, index) => <motion.div key={index} className="bg-navy-light border border-gold/30 hover:border-gold p-4 md:p-6 rounded-lg text-center transition-all duration-300" initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-343">
                  <p className="font-heading text-sm sm:text-base md:text-lg font-semibold text-gold" data-id="element-344">
                    {commodity}
                  </p>
                </motion.div>)}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="bg-ivory py-16 md:py-20" data-id="element-345">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-346">
          <motion.div className="text-center mb-12 md:mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-347">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-348">
              Global Reach
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-4 md:mb-6" data-id="element-349" />
            <p className="text-base md:text-lg text-charcoal max-w-2xl mx-auto" data-id="element-350">
              Connecting opportunities across international markets
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-351">
            {[{
            region: 'Canada',
            description: 'Canada-based operations with deep expertise in North American mining sectors.',
            icon: MapPin
          }, {
            region: 'Middle East',
            description: 'Strategic relationships with buyers and investors across Middle Eastern markets.',
            icon: MapPin
          }, {
            region: 'International',
            description: 'Extensive network spanning global commodity trading and investment hubs.',
            icon: MapPin
          }].map((item, index) => <motion.div key={index} className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15
          }} data-id="element-352">
                <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-gold rounded-full mb-4" data-id="element-353">
                  <item.icon className="h-7 w-7 md:h-8 md:w-8 text-navy" data-id="element-354" />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-2 md:mb-3" data-id="element-355">
                  {item.region}
                </h3>
                <p className="text-charcoal leading-relaxed text-sm md:text-base" data-id="element-356">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-navy py-16 md:py-20" data-id="element-357">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-358">
          <motion.div className="max-w-3xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-359">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6" data-id="element-360">
              Discuss a Strategic Opportunity
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-361" />
            <p className="text-base md:text-lg text-steel mb-8 leading-relaxed" data-id="element-362">
              Connect with our team to explore investment opportunities, mining
              project partnerships, or strategic off-take arrangements.
            </p>
            <Button asChild size="lg" className="bg-gold hover:bg-gold-dark text-navy font-semibold whitespace-nowrap" data-id="element-363">
              <Link to="/contact" className="inline-flex items-center justify-center" data-id="element-364">
                Schedule a Consultation
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>;
}